import { Cell, Column, DataGrid, HeaderCell } from '@veeva/corgix/data-grid';
import Dialog from '@veeva/corgix/dialog';
import { useContext, useEffect, useState } from 'react';
import { css, ThemeProvider, useTheme } from '@emotion/react';
import Button from '@veeva/corgix/button';
import Link from '../../../components/Link';

import MyAccountLayout from '../../../components/MyAccountLayout';
import NavigationTabs from '../../../constants/NavigationTabs';
import getRecentActivityData from '../../../services/getRecentActivityData';
import useMessageService from '../../../services/i18n/useMessageService';
import LocalizedMessage from '../../../components/LocalizedMessage';
import { AuthenticatedPaths } from '../../../constants/Paths';
import { getInitialSort, getSortedAlphaNumericValues } from '../../../services/storageSortingUtils';
import LocalStorageItems from '../../../constants/LocalStorageItems';
import getLastLoginContent from '../../../services/getLastLoginContent';
import AuthenticatedRouteLanguageContext from '../../../services/AuthenticatedRouteLanguageContext';
import useLanguageLocaleTimezoneValues from '../../../services/useLanguageLocaleTimezoneValues';
import useNotificationCookie from '../../../services/useNotificationCookie';
import HelpIcon from '../../../components/HelpIcon';
import appendFormattedTime from '../../../services/appendFormattedTime';

const defaultRecentActivitySort = ['lastLogin', 'desc'];
const defaultLoginDetailsSort = ['loginDate', 'desc'];

const RecentActivity = () => {
    const { grayLighter } = useTheme();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [recentActivities, setRecentActivities] = useState([]);
    const [loading, setLoading] = useState(false);

    const [recentActivitySort, setRecentActivitySort] = useState(
        getInitialSort(LocalStorageItems.RECENTACTIVITYGRIDSORT, defaultRecentActivitySort),
    );
    const [loginDetailsSort, setLoginDetailsSort] = useState(
        getInitialSort(LocalStorageItems.LOGINDETAILGRIDSORT, defaultLoginDetailsSort),
    );

    const [recentActivtySortColumnKey, recentActivitySortDir] = recentActivitySort;
    const [loginDetailsSortColumnKey, loginDetailsSortDir] = loginDetailsSort;
    const { messageLanguage } = useContext(AuthenticatedRouteLanguageContext);

    const notificationCookie = useNotificationCookie();
    const { timezone } = useLanguageLocaleTimezoneValues(notificationCookie);

    const [
        [
            statusLabel,
            operatingSystemLabel,
            browserLabel,
            lastLoginLabel,
            lastLoginLocationLabel,
            totalLoginsLabel,
            timeLabel,
            ipAddressLabel,
            recentActivityDescription1Label,
            recentActivityDescription2Label,
            closeLabel,
            recentActivityHelpAriaLabel,
        ],
        messagesLoading,
    ] = useMessageService([
        'my_account.status',
        'my_account.operating_system',
        'my_account.browser',
        'my_account.last_login',
        'my_account.last_login_location',
        'my_account.total_logins',
        'my_account.time',
        'my_account.ip_address',
        'my_account.recent_activity_description_1',
        'my_account.recent_activity_description_2',
        'general.close',
        'my_account.recent_activity_help_message_aria_label',
    ]);

    useEffect(() => {
        const fetchRecentActivityData = async () => {
            setLoading(true);
            const fetchedRecentActivityData = await getRecentActivityData();

            setRecentActivities(fetchedRecentActivityData);
            setLoading(false);
        };
        fetchRecentActivityData();
    }, []);

    useEffect(() => {
        localStorage.setItem(
            LocalStorageItems.RECENTACTIVITYGRIDSORT,
            JSON.stringify(recentActivitySort),
        );
    }, [recentActivitySort]);

    const handleRecentActivityGridColumnSort = (columnKey, columnId, dir) => {
        setRecentActivitySort([columnKey, dir]);
    };

    const getRecentActivityGridColumnSortDirection = (columnKey) => {
        return recentActivtySortColumnKey === columnKey ? recentActivitySortDir : undefined;
    };

    useEffect(() => {
        localStorage.setItem(
            LocalStorageItems.LOGINDETAILGRIDSORT,
            JSON.stringify(loginDetailsSort),
        );
    }, [loginDetailsSort]);

    const handleLoginDetailsGridColumnSort = (columnKey, columnId, dir) => {
        setLoginDetailsSort([columnKey, dir]);
    };

    const getLoginDetailsGridColumnSortDirection = (columnKey) => {
        return loginDetailsSortColumnKey === columnKey ? loginDetailsSortDir : undefined;
    };

    const displayedRecentActivites = getSortedAlphaNumericValues(
        recentActivities,
        recentActivtySortColumnKey,
        recentActivitySortDir,
    );

    const displayedLoginDetails = getSortedAlphaNumericValues(
        selectedRowData?.recentActivityDetailList,
        loginDetailsSortColumnKey,
        loginDetailsSortDir,
    );

    const handleOSCellClick = (rowIndex) => {
        setSelectedRowData(displayedRecentActivites[rowIndex]);
        setIsDialogOpen(true);
    };

    const renderTextCell = ({ cellData }) => <Cell wrap>{cellData}</Cell>;

    const renderLoginTimeCell = (loginDate, loginFormatted) => {
        return <Cell wrap>{getLastLoginContent(loginDate, loginFormatted, messageLanguage)}</Cell>;
    };

    const renderLoginDetailsTimeCell = ({ cellData, rowIndex }) => {
        const record = displayedLoginDetails[rowIndex];
        return (
            <Cell wrap>
                {appendFormattedTime(record.loginDate, record.loginFormatted, {
                    locale: messageLanguage,
                    timezone,
                })}
            </Cell>
        );
    };

    const renderOperatingSystemCell = ({ cellData, rowIndex }) => {
        return (
            <Cell wrap>
                <Link
                    onClick={() => {
                        handleOSCellClick(rowIndex);
                    }}
                    css={css`
                        font-size: 1rem;
                    `}
                >
                    {cellData}
                </Link>
            </Cell>
        );
    };

    const renderRecentActivityDescription = () => {
        return (
            <ThemeProvider theme={{ linkFontSize: '1rem' }}>
                <span
                    css={css`
                        font-size: 1rem;
                    `}
                >
                    <LocalizedMessage
                        template={recentActivityDescription1Label}
                        tokens={[
                            <Link
                                key="change-password-link"
                                href={AuthenticatedPaths.CHANGE_PASSWORD}
                            >
                                {recentActivityDescription2Label}
                            </Link>,
                        ]}
                    />
                </span>
            </ThemeProvider>
        );
    };

    const formatDialogTitle = () => {
        return `${selectedRowData?.platform} (${selectedRowData?.userAgent}) - ${selectedRowData?.country}`;
    };
    const recentActivityHelpIconCSS = css`
        position: absolute;
        width: 2rem;
        height: 2rem;
        padding-left: 0.3333rem;

        & > svg:first-of-type {
            width: 2rem;
            height: 2rem;
        }
    `;

    const recentActivityHelpIconTextCSS = css`
        font-size: 1.1666rem;
        color: ${grayLighter};
    `;

    const hamburgerHeaderContent = () => {
        if (messagesLoading) {
            return null;
        }

        return (
            <HelpIcon
                ariaLabel={recentActivityHelpAriaLabel}
                helpIconCSS={recentActivityHelpIconCSS}
                message={
                    <span css={recentActivityHelpIconTextCSS}>
                        {renderRecentActivityDescription()}
                    </span>
                }
            />
        );
    };

    const renderDialogTitle = () => {
        return (
            <div
                css={css`
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                `}
            >
                {formatDialogTitle()}
            </div>
        );
    };

    return (
        <MyAccountLayout
            childrenLoading={messagesLoading}
            headerDescription={renderRecentActivityDescription()}
            mobileHeaderContent={hamburgerHeaderContent()}
            responsiveLayout
            selectedTab={NavigationTabs.RECENT_ACTIVITY}
        >
            <DataGrid
                css={css`
                    flex-grow: 1;
                    overflow-y: auto;
                `}
                data={displayedRecentActivites}
                height="100%"
                loading={loading}
                onColumnSort={handleRecentActivityGridColumnSort}
                spanFullGrid
                width="100%"
            >
                <Column
                    cell={renderTextCell}
                    columnKey="status"
                    header={<HeaderCell>{statusLabel}</HeaderCell>}
                    resizable
                    sortable
                    sortDirection={getRecentActivityGridColumnSortDirection('status')}
                    width={100}
                />
                <Column
                    cell={renderOperatingSystemCell}
                    columnKey="platform"
                    header={<HeaderCell>{operatingSystemLabel}</HeaderCell>}
                    resizable
                    sortable
                    sortDirection={getRecentActivityGridColumnSortDirection('platform')}
                    width={200}
                />
                <Column
                    cell={renderTextCell}
                    columnKey="userAgent"
                    header={<HeaderCell>{browserLabel}</HeaderCell>}
                    resizable
                    sortable
                    sortDirection={getRecentActivityGridColumnSortDirection('userAgent')}
                    width={200}
                />
                <Column
                    cell={({ cellData, rowIndex }) => {
                        return renderLoginTimeCell(
                            cellData,
                            displayedRecentActivites[rowIndex].lastLoginFormatted,
                        );
                    }}
                    columnKey="lastLogin"
                    header={<HeaderCell>{lastLoginLabel}</HeaderCell>}
                    resizable
                    sortable
                    sortDirection={getRecentActivityGridColumnSortDirection('lastLogin')}
                    width={150}
                />
                <Column
                    cell={renderTextCell}
                    columnKey="country"
                    header={<HeaderCell>{lastLoginLocationLabel}</HeaderCell>}
                    resizable
                    sortable
                    sortDirection={getRecentActivityGridColumnSortDirection('country')}
                    width={150}
                />
                <Column
                    cell={renderTextCell}
                    columnKey="loginCount"
                    header={<HeaderCell>{totalLoginsLabel}</HeaderCell>}
                    resizable
                    sortable
                    sortDirection={getRecentActivityGridColumnSortDirection('loginCount')}
                    width={150}
                />
            </DataGrid>
            <ThemeProvider theme={{ buttonBorderRadius: '0.3333rem' }}>
                <Dialog
                    actions={[
                        <Button
                            css={({ orangeDefault }) => css`
                                &:focus-visible {
                                    outline: 2px solid ${orangeDefault};
                                }
                            `}
                            key="close-button"
                            onClick={() => setIsDialogOpen(false)}
                            variant="primary"
                        >
                            {closeLabel}
                        </Button>,
                    ]}
                    onClose={() => {
                        setIsDialogOpen(false);
                    }}
                    open={isDialogOpen}
                    size="sm"
                    title={renderDialogTitle()}
                >
                    <DataGrid
                        data={displayedLoginDetails}
                        onColumnSort={handleLoginDetailsGridColumnSort}
                        spanFullGrid
                    >
                        <Column
                            cell={renderLoginDetailsTimeCell}
                            columnKey="loginDate"
                            header={<HeaderCell>{timeLabel}</HeaderCell>}
                            resizable
                            sortable
                            sortDirection={getLoginDetailsGridColumnSortDirection('loginDate')}
                            width={180}
                        />
                        <Column
                            cell={renderTextCell}
                            columnKey="sourceIp"
                            header={<HeaderCell>{ipAddressLabel}</HeaderCell>}
                            resizable
                            sortable
                            width={180}
                        />
                    </DataGrid>
                </Dialog>
            </ThemeProvider>
        </MyAccountLayout>
    );
};

export default RecentActivity;
